import { useControlledState } from "@react-stately/utils";
import { Percent01 } from "@untitledui/icons";
import { HintText } from "@/components/base/input/hint-text";
import type { InputBaseProps } from "@/components/base/input/input";
import { InputBase, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";

export const formatPercentage = (number: string) => {
  const cleaned = number.replace(/\D/g, "");
  const sliced =
    cleaned.length > 2
      ? cleaned === "100"
        ? cleaned
        : cleaned.slice(0, 2)
      : cleaned;

  const percentage = parseInt(sliced, 10);
  if (percentage > 100) {
    return "100";
  }
  if (percentage < 0) {
    return "0";
  }
  if (Number.isNaN(percentage) || number === "") {
    return "";
  }

  return percentage.toString();
};

export interface PercentageInputProps
  extends Omit<InputBaseProps, "maxLength"> {}

export const PercentageInput = ({
  onChange,
  value,
  defaultValue,
  className,
  label,
  hint,
  "aria-label": ariaLabelProp,
  id,
  name,
  ...props
}: PercentageInputProps) => {
  const [percentageValue, setPercentageValue] = useControlledState(
    value,
    defaultValue || "",
    (value) => {
      const cleaned = value.replace(/\D/g, "");
      onChange?.(cleaned || "");
    },
  );

  return (
    <TextField
      aria-label={ariaLabelProp || label || props?.placeholder}
      {...props}
      className={className}
      inputMode="numeric"
      maxLength={3}
      value={formatPercentage(percentageValue)}
      onChange={setPercentageValue}
    >
      {({ isDisabled, isInvalid, isRequired }) => (
        <>
          {label && <Label isRequired={isRequired}>{label}</Label>}

          <InputBase
            {...props}
            id={id}
            name={name}
            isDisabled={isDisabled}
            isInvalid={isInvalid}
            icon={Percent01}
            inputClassName="pl-13"
            iconClassName="left-2.5 h-6 w-8.5"
          />

          {hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}
        </>
      )}
    </TextField>
  );
};

PercentageInput.displayName = "PercentageInput";
